import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../store/store';
import type { GuildStatus } from '../../types';

interface GuildsState {
  availableGuilds: GuildStatus[];
  selectedGuild: string | null;
  isAdmin: boolean;
}

const initialState: GuildsState = {
  availableGuilds: [],
  selectedGuild: null,
  isAdmin: false,
};

export const { actions, reducer } = createSlice({
  name: 'guilds',
  initialState,
  reducers: {
    setAvailableGuilds: (state, action: PayloadAction<GuildStatus[]>) => {
      state.availableGuilds = action.payload;
    },
    setSelectedGuild: (state, action: PayloadAction<string>) => {
      state.selectedGuild = action.payload;
      const guildStatus = state.availableGuilds.find(
        (g) => g.guild.id === action.payload
      );
      state.isAdmin = guildStatus?.isAdmin || false;
    },
    clearSelectedGuild: (state) => {
      state.selectedGuild = null;
      state.isAdmin = false;
    },
  },
});

//Define action interfaces for the custom hook
interface GuildsActions {
  setAvailableGuilds: (guilds: GuildStatus[]) => void;
  setSelectedGuild: (guildId: string) => void;
  clearSelectedGuild: () => void;
}

// Combined type for the custom hook
type GuildsSlice = GuildsState & GuildsActions;

// Custom hook that combines state and actions
export const useGuildsSlice = (): GuildsSlice => {
  const dispatch = useDispatch();
  return {
    // State selectors
    availableGuilds: useSelector((state: RootState) => state.guilds.availableGuilds),
    selectedGuild: useSelector((state: RootState) => state.guilds.selectedGuild),
    isAdmin: useSelector((state: RootState) => state.guilds.isAdmin),

    // Action dispatchers
    setAvailableGuilds: (guilds) => dispatch(actions.setAvailableGuilds(guilds)),
    setSelectedGuild: (guildId) => dispatch(actions.setSelectedGuild(guildId)),
    clearSelectedGuild: () => dispatch(actions.clearSelectedGuild()),
  };
};

export default reducer;
